import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { useState, useEffect } from "react";
import { AlertTriangle, Loader2, ShieldAlert } from "lucide-react";
import {
  useResumoQuinzena, useFecharQuinzena, useAdicionaisDaQuinzena, useMensalistasDaQuinzena,
  useGerarAdiantamentos,
} from "@/hooks/use-equipe";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import type { Quinzena } from "@shared/schema";
import {
  formatarResumoAdicional, paraReais,
  agruparAdicionaisPorFuncionario, totalAdicionaisCentavos as somarLinhasAdicional,
  totalAdiantamentosMensalistasCentavos, separarResumoQuinzena,
  separarMensalistasPorAdiantamento, competenciaDaQuinzena, rotuloSinalLancamento,
  adiantamentoSaiNestaQuinzena,
  lancamentosNoCaixaDaQuinzena,
} from "@shared/equipe-fechamento";

interface FecharQuinzenaDialogProps {
  open: boolean;
  onClose: () => void;
  quinzena: Quinzena;
}

/** Mensagem do servidor a partir do erro da requisição ("409: {json}"). */
function msgDoErro(e: unknown, fallback: string): string {
  const bruto = String((e as any)?.message ?? "");
  try {
    const j = JSON.parse(bruto.slice(bruto.indexOf(":") + 1).trim());
    if (typeof j?.error === "string") return j.error;
  } catch {
    // Corpo não-JSON: cai no fallback. Nunca engolir o erro em silêncio.
  }
  return fallback;
}

export function FecharQuinzenaDialog({ open, onClose, quinzena }: FecharQuinzenaDialogProps) {
  const { data: resumo, isLoading } = useResumoQuinzena(open ? quinzena.id : undefined);
  // Fase 3 — a pré-visualização de fechamento precisa incluir os adicionais:
  // sem isto, o gestor confirma o fechamento vendo um total menor do que o que
  // vai efetivamente sair na quinzena.
  const { data: adicionaisQuinzena } = useAdicionaisDaQuinzena(open ? quinzena.id : undefined);
  // §2 (v2.10) — MESMO hook que BlocoMensalistas.tsx/financeiro.tsx já usam pro
  // bloco "Mensalistas e encarregados" da tela principal. Não é query nova: é
  // reuso deliberado pra não repetir o bug do #119 (duas fontes calculando a
  // mesma coisa com critério diferente).
  const { data: mensalistas = [] } = useMensalistasDaQuinzena(open ? quinzena.id : undefined);
  const fechar = useFecharQuinzena();
  const gerarAdiantamentos = useGerarAdiantamentos();
  const { toast } = useToast();

  // §2.2 — uma linha por (pessoa, tipo): nunca soma COLHEITA e OUTRO juntos.
  // D-M4 (Financeiro v3) — desconto de MENSALISTA liquida no fechamento do mês,
  // nunca aqui: filtra ANTES de agrupar, senão a prévia abate duas vezes.
  const adicionalPorFuncionario = agruparAdicionaisPorFuncionario(
    lancamentosNoCaixaDaQuinzena(adicionaisQuinzena?.agregado ?? []),
  );

  const handleFechar = async () => {
    try {
      await fechar.mutateAsync(quinzena.id);
      toast({ title: "Quinzena fechada com sucesso!" });
      onClose();
    } catch (e) {
      // Mensagem REAL do servidor — o genérico escondia os 409 que dizem o que
      // precisa ser resolvido antes de fechar.
      toast({ title: msgDoErro(e, "Erro ao fechar quinzena"), variant: "destructive" });
    }
  };

  const rows = Array.isArray(resumo) ? (resumo as any[]) : [];
  // Mensalista é escopo À PARTE (própria seção abaixo), nunca "sem lançamento"
  // por causa da separação estrutural de `getResumoQuinzenaPorFuncionario`
  // (§4.5) — ver `separarResumoQuinzena` em shared/equipe-fechamento.ts.
  const mensalistaIds = new Set(mensalistas.map(m => m.id));
  const { comLancamento, semLancamento } = separarResumoQuinzena(
    rows, mensalistaIds, id => adicionalPorFuncionario.has(id),
  );
  const nOcultos = semLancamento.length;
  const totalAdicionaisCentavos = comLancamento.reduce(
    (acc: number, r: any) => acc + somarLinhasAdicional(adicionalPorFuncionario.get(r.id)), 0
  );
  const totalDiarias = comLancamento.reduce((acc: number, r: any) => acc + parseFloat(r.total_bruto || 0), 0);

  // §2 — quem tem adiantamento (qualquer status) entra na lista; a soma usa a
  // MESMA função do rodapé da tela principal (financeiro.tsx), sobre o MESMO
  // array de `mensalistas` — garante bater exato por construção, não por
  // coincidência do dado de hoje.
  // Raiz do #122: ausência de registro lida como ausência de direito.
  // `semAdiantamento` é quem É candidato (MENSALISTA + QUINZENAL) mas ainda
  // não tem adiantamento gerado nesta competência.
  const [confirmacao, setConfirmacao] = useState("");
  useEffect(() => { if (!open) setConfirmacao(""); }, [open]);

  // `comAdiantamento` = adiantamento da COMPETÊNCIA resolvido (pode ter saído na
  // OUTRA quinzena do mês). A prévia mostra e conta só o que sai NESTA — caixa.
  const { comAdiantamento, semAdiantamento } = separarMensalistasPorAdiantamento(mensalistas);
  const linhasMensalistas = comAdiantamento.filter(adiantamentoSaiNestaQuinzena);
  // Regra de não-regressão: sem pendência, fechar é o MESMO clique de hoje.
  const temPendencia = !isLoading && semAdiantamento.length > 0;
  const bloqueado = temPendencia && confirmacao.trim().toUpperCase() !== "FECHAR";
  const totalMensalistasCentavos = totalAdiantamentosMensalistasCentavos(mensalistas);
  // D13 — só quem ainda está PREVISTO vira PAGO na cascata do fechamento.
  const nAdiantamentosAPagar = linhasMensalistas.filter(m => m.adiantamento_status === "PREVISTO").length;

  const totalGeral = totalDiarias + totalAdicionaisCentavos / 100 + totalMensalistasCentavos / 100;
  const totalDias = comLancamento.reduce((acc: number, r: any) => acc + parseFloat(r.dias_trabalhados || 0), 0);

  const fmtDias = (n: any) => {
    const v = parseFloat(String(n ?? 0));
    return Number.isInteger(v) ? String(v) : v.toFixed(1).replace(".", ",");
  };

  const handleGerarPendentes = async () => {
    try {
      await gerarAdiantamentos.mutateAsync({
        quinzenaId: quinzena.id,
        competencia: competenciaDaQuinzena(quinzena),
        itens: semAdiantamento.map(s => ({ funcionarioId: s.mensalista.id, valor: s.valorSugerido })),
      });
      toast({ title: "Adiantamentos gerados!" });
      // Não fecha o modal — a prévia atualiza sozinha (query invalidada pelo hook).
    } catch (e) {
      toast({ title: msgDoErro(e, "Erro ao gerar adiantamentos"), variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl p-0 gap-0 max-h-[85vh] flex flex-col overflow-hidden">
        {/* Zona 1 — header fixo */}
        <div className="shrink-0 p-5 border-b space-y-3">
          <DialogHeader className="space-y-1">
            <DialogTitle>Fechar Quinzena</DialogTitle>
            <p className="text-sm text-muted-foreground">
              {format(new Date(quinzena.periodoInicio + "T00:00:00"), "dd/MM/yyyy")} – {format(new Date(quinzena.periodoFim + "T00:00:00"), "dd/MM/yyyy")}
            </p>
          </DialogHeader>

          {/* resumo */}
          <div className="flex items-center gap-5 rounded-lg border border-sucesso/30 bg-sucesso-suave px-4 py-3">
            <div className="flex flex-col pr-5 border-r border-sucesso/30">
              <span className="text-base font-bold leading-none">{comLancamento.length + linhasMensalistas.length}</span>
              <span className="text-[11px] text-muted-foreground mt-1">funcionários</span>
            </div>
            <div className="flex flex-col pr-5 border-r border-sucesso/30">
              <span className="text-base font-bold leading-none">{fmtDias(totalDias)}</span>
              <span className="text-[11px] text-muted-foreground mt-1">dias</span>
            </div>
            <div className="flex flex-col items-end ml-auto text-right">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-sucesso">Total Geral</span>
              <span className="text-xl font-extrabold text-sucesso leading-none mt-0.5">R$ {totalGeral.toFixed(2)}</span>
            </div>
          </div>

          {/* aviso */}
          <div className="flex items-start gap-2 rounded-md bg-atencao-suave border border-atencao/30 text-atencao text-sm p-2.5">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Após fechar, não será possível adicionar ou remover lançamentos.</span>
          </div>
        </div>

        {/* Zona 2 — corpo rolável */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="space-y-2 p-5">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-8" />)}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="sticky top-0 bg-background">Funcionário</TableHead>
                  <TableHead className="sticky top-0 bg-background text-right">Dias</TableHead>
                  <TableHead className="sticky top-0 bg-background text-right">Diárias</TableHead>
                  <TableHead className="sticky top-0 bg-background text-right">Adicionais</TableHead>
                  <TableHead className="sticky top-0 bg-background text-right">Total</TableHead>
                  <TableHead className="sticky top-0 bg-background">PIX</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comLancamento.map((r: any) => {
                  const adicionais = adicionalPorFuncionario.get(r.id);
                  const totalLinha = parseFloat(r.total_bruto) + somarLinhasAdicional(adicionais) / 100;
                  return (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">{r.apelido ?? r.nome}</TableCell>
                      <TableCell className="text-right">{fmtDias(r.dias_trabalhados)}</TableCell>
                      <TableCell className="text-right">R$ {parseFloat(r.total_bruto).toFixed(2)}</TableCell>
                      <TableCell className="text-right">
                        {adicionais?.length ? (
                          <div className="space-y-1">
                            {adicionais.map(linha => (
                              <div key={`${linha.tipo}-${linha.sinal}`}>
                                {/* Desconto NUNCA pode aparecer com "+": é o
                                    mesmo valor positivo do banco com sentido
                                    oposto (migração 046). */}
                                <span className={linha.sinal === "DESCONTO" ? "text-erro font-medium" : "text-sucesso font-medium"}>
                                  {linha.sinal === "DESCONTO" ? "−" : "+"} R$ {(linha.totalCentavos / 100).toFixed(2)}
                                </span>
                                <p className="text-[10px] text-muted-foreground truncate">
                                  {formatarResumoAdicional({
                                    qtd: linha.qtd, kgTotal: linha.kgTotal,
                                    totalValor: paraReais(linha.totalCentavos),
                                    tipoLabel: rotuloSinalLancamento(linha.sinal).toLowerCase(),
                                  })}
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-right font-semibold">R$ {totalLinha.toFixed(2)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{r.pix_chave ?? "—"}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {/* §2 (v2.10) — mensalistas/encarregados com adiantamento nesta quinzena.
              Fora da Table acima de propósito: não têm "dias trabalhados" nem PIX
              no mesmo sentido — o valor deles é adiantamento (parte do salário),
              não diária, e precisa ficar claramente separado disso. */}
          {!isLoading && linhasMensalistas.length > 0 && (
            <div className="p-5 pt-3 border-t space-y-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Mensalistas e encarregados
              </h3>
              <div className="space-y-1.5">
                {linhasMensalistas.map(m => (
                  <div key={m.id} className="flex items-center justify-between gap-2 text-sm">
                    <span className="font-medium">{m.apelido ?? m.nome}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-muted-foreground">adiantamento</span>
                      <span className="font-semibold">R$ {parseFloat(m.adiantamento_valor ?? "0").toFixed(2)}</span>
                      {m.adiantamento_status !== "PREVISTO" && (
                        <Badge variant="outline" className="text-[10px]">{m.adiantamento_status}</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {nAdiantamentosAPagar > 0 && (
                <p className="text-[11px] text-muted-foreground">
                  {nAdiantamentosAPagar === 1
                    ? "O adiantamento acima será marcado como pago."
                    : `Os ${nAdiantamentosAPagar} adiantamentos acima serão marcados como pagos.`}
                </p>
              )}
            </div>
          )}

          {/* Raiz do bug: ausência de registro lida como ausência de direito
              (mesma classe do #122). A faixa era ÂMBAR e passiva — existia em
              agosto, foi lida, e o fechamento passou mesmo assim. Agora é
              checklist BLOQUEANTE: ou gera, ou digita FECHAR no rodapé. */}
          {!isLoading && semAdiantamento.length > 0 && (
            <div className="p-5 pt-3 border-t space-y-2">
              <div className="flex items-start gap-2 rounded-md bg-erro-suave border-2 border-erro/30 text-erro text-sm p-2.5">
                <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  {semAdiantamento.length === 1
                    ? "1 mensalista sem adiantamento gerado nesta competência — vai receber só os adicionais, e o fechamento do mês pagaria o salário cheio."
                    : `${semAdiantamento.length} mensalistas sem adiantamento gerado nesta competência — vão receber só os adicionais, e o fechamento do mês pagaria o salário cheio.`}
                </span>
              </div>
              <div className="space-y-1.5">
                {semAdiantamento.map(({ mensalista: m, valorSugerido }) => (
                  <div key={m.id} className="flex items-center justify-between gap-2 text-sm">
                    <span className="font-medium">{m.apelido ?? m.nome}</span>
                    <span className="font-semibold text-erro">R$ {parseFloat(valorSugerido).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="w-full border-erro/30 bg-erro-suave hover:bg-erro/20 text-erro"
                disabled={gerarAdiantamentos.isPending}
                onClick={handleGerarPendentes}
              >
                {gerarAdiantamentos.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                {gerarAdiantamentos.isPending ? "Gerando..." : "Gerar agora"}
              </Button>
            </div>
          )}
        </div>

        {/* Zona 3 — rodapé fixo */}
        <div className="shrink-0 border-t p-4 flex items-center justify-between gap-3">
          {nOcultos > 0 ? (
            <span className="text-xs text-muted-foreground">{nOcultos} sem lançamento ocultado{nOcultos > 1 ? "s" : ""}</span>
          ) : <span />}
          <div className="flex items-center gap-2">
            {/* Escape do bloqueio: fechar com pendência continua possível, mas
                nunca com um clique só. Sem pendência, nada muda. */}
            {temPendencia && (
              <input
                value={confirmacao}
                onChange={e => setConfirmacao(e.target.value)}
                placeholder="digite FECHAR"
                aria-label="Digite FECHAR para confirmar"
                className="w-32 rounded-md border border-erro/30 px-2 py-1.5 text-sm uppercase tracking-wide"
              />
            )}
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button variant="destructive" onClick={handleFechar} disabled={fechar.isPending || bloqueado}>
              {fechar.isPending ? "Fechando..." : "Confirmar Fechamento"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
